
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { NodeContainer } from '../base/NodeContainer'
import { withNodeOptimization } from '../hoc/withNodeOptimization'
import { NODE_DIMENSIONS, NODE_VISUAL_CONSTANTS, resolveNodeBox } from '@genieartinc/schemas'
import { useContentAutoSize } from '../base/use-content-auto-size'
import { Icon } from '@/components/ui/icon'
import { useCanvasFiles } from '@/hooks/useCanvasFiles'
import { useParams } from '@tanstack/react-router'
import { readLibraryFileDrop } from '@/components/canvas/nodes/utils/library-file-drop'

interface ImageVersionInfo {
  url: string
  width: number
  height: number
  size: number
}

interface FileMetadata {
  filename?: string
  publicUrl?: string
  width?: number
  height?: number
  versions?: {
    original?: ImageVersionInfo
    lowRes?: ImageVersionInfo
    preview?: ImageVersionInfo
  }
}

interface ImageFileProps {
  id: string
  title?: string
  selected?: boolean
  fileUuid?: string // NEW: File UUID from database
  canvasId?: string // Canvas ID for upload
  zoom?: number // Canvas zoom level for optimization (from Magic Canvas)
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  onFileChange?: (fileUuid: string, metadata: {
    filename: string
    size: number
    contentType: string
    publicUrl: string
    width?: number
    height?: number
    versions?: {
      original?: ImageVersionInfo
      lowRes?: ImageVersionInfo
      preview?: ImageVersionInfo
    }
  }) => void
  onDimensionsChange?: (dimensions: { width: number; height: number }) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
  hasParent?: boolean // Whether this node has a parent (generated from AI node)
}

function ImageFileComponent({
  id,
  title,
  selected = false,
  fileUuid,
  canvasId: propCanvasId,
  zoom,
  onSelect,
  onHoverChange,
  onFileChange,
  onDimensionsChange,
  onStateChange,
  state = {},
  hasParent = false,
}: ImageFileProps) {
  const { id: canvasIdParam } = useParams({ strict: false })
  const canvasId = propCanvasId || (canvasIdParam as string)

  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isLoadingImage, setIsLoadingImage] = useState(false)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)
  const lastReportedMetadata = useRef<{ width?: number; height?: number } | null>(null)
  const isUploadingRef = useRef(false)

  const { uploadFile, isUploading, uploadProgress, error } = useCanvasFiles()

  // Track which version is currently displayed. The box follows content aspect
  // unless the user has resized; the image itself uses object-contain.
  const fileMetadata = state?.fileMetadata as FileMetadata | undefined
  const lowResUrl = fileMetadata?.versions?.lowRes?.url
  const originalUrl = fileMetadata?.versions?.original?.url || fileMetadata?.publicUrl

  useEffect(() => {
    const currentZoom = zoom ?? 1.0

    // Use low-res when zoomed out, original when zoomed in or selected
    const shouldUseLowRes = currentZoom < 1.0 && !selected
    const urlToUse = (shouldUseLowRes && lowResUrl) ? lowResUrl : originalUrl

    if (urlToUse) {
      setImageUrl(urlToUse)
      setIsImageLoaded(false)
    } else {
      setImageUrl(null)
      setIsImageLoaded(false)
    }
  }, [id, lowResUrl, originalUrl, zoom, selected])

  // Handle file upload (shared logic for button click and drag-drop)
  const uploadImageFile = useCallback(async (file: File) => {
    if (isUploadingRef.current) return
    isUploadingRef.current = true

    if (!canvasId) {
      isUploadingRef.current = false
      console.error('Canvas ID is required for file upload')
      return
    }

    // CRITICAL: Set loading state at the start
    setIsLoadingImage(true)

    try {
      // Get image dimensions before upload
      const img = new Image()
      const dimensionsPromise = new Promise<{ width: number; height: number }>((resolve, reject) => {
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight })
          URL.revokeObjectURL(img.src)
        }
        img.onerror = () => {
          URL.revokeObjectURL(img.src)
          reject(new Error('Failed to load image'))
        }
      })
      img.src = URL.createObjectURL(file)
      const dimensions = await dimensionsPromise

      // Upload to backend with dimensions in metadata
      const uploadedFile = await uploadFile(canvasId, {
        file,
        nodeId: id, // Associate with this node
        metadata: {
          width: dimensions.width,
          height: dimensions.height,
        },
      })

      if (!uploadedFile) {
        throw new Error('Upload failed - no file returned')
      }

      // Update node state with fileUuid and FULL metadata (including versions)
      const uploadedMetadata = (uploadedFile.metadata || {}) as FileMetadata;

      onFileChange?.(uploadedFile.uuid, {
        filename: uploadedFile.filename,
        size: uploadedFile.size,
        contentType: uploadedFile.contentType,
        publicUrl: uploadedFile.publicUrl || '',
        width: uploadedMetadata.width || dimensions.width,
        height: uploadedMetadata.height || dimensions.height,
        versions: uploadedMetadata.versions,
      })

      // Set image URL for preview
      if (uploadedFile.publicUrl) {
        setImageUrl(uploadedFile.publicUrl)
      }
    } catch (err) {
      console.error('Failed to upload image:', err)
    } finally {
      // CRITICAL: Always clear loading state (runs after try OR catch)
      setIsLoadingImage(false)
      isUploadingRef.current = false
    }
  }, [canvasId, id, uploadFile, onFileChange])

  // Handle image upload via button click
  const handleImageUpload = useCallback(async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      await uploadImageFile(file)
    }

    input.click()
  }, [uploadImageFile])

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    // A file dragged from the File Manager is already uploaded: link it instead.
    // Its thumbnail can ride along as a File, which must not be re-uploaded.
    const library = readLibraryFileDrop(e.dataTransfer)
    const file = library ? undefined : e.dataTransfer.files?.[0]
    if (!file) {
      if (!library || !library.contentType.startsWith('image/')) return
      onFileChange?.(library.uuid, { filename: library.filename, size: 0, contentType: library.contentType, publicUrl: library.publicUrl })
      setImageUrl(library.publicUrl)
      return
    }

    // Check if it's an image file
    if (!file.type.startsWith('image/')) {
      return
    }

    await uploadImageFile(file)
  }, [uploadImageFile, onFileChange])

  // Handle image removal
  const handleImageRemove = useCallback(async () => {
    setIsLoadingImage(true)
    try {
      setImageUrl(null)
      onFileChange?.('', { filename: '', size: 0, contentType: '', publicUrl: '' })
    } finally {
      setIsLoadingImage(false)
    }
  }, [onFileChange])

  // Report dimensions only on image load (not on zoom/scale changes)
  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true)
  }, [])

  // Calculate and report dimensions based on metadata
  // Extract metadata to satisfy ESLint (avoid re-render on full metadata object changes)
  const fileMetadataDims = state?.fileMetadata as FileMetadata | undefined
  const metadataWidth = fileMetadataDims?.width
  const metadataHeight = fileMetadataDims?.height
  const reportAspect = useContentAutoSize(id, 'ImageFile')

  useEffect(() => {
    if (state.sizeSource === 'manual') return
    if (typeof metadataWidth !== 'number' || metadataWidth <= 0 || typeof metadataHeight !== 'number' || metadataHeight <= 0) return
    const prevMetadata = lastReportedMetadata.current
    if (prevMetadata && prevMetadata.width === metadataWidth && prevMetadata.height === metadataHeight) return
    lastReportedMetadata.current = { width: metadataWidth, height: metadataHeight }
    const aspect = metadataHeight / metadataWidth
    reportAspect(aspect)
    const box = resolveNodeBox({
      componentType: 'ImageFile',
      savedWidth: NODE_DIMENSIONS.ImageFile.width,
      savedHeight: NODE_DIMENSIONS.ImageFile.height,
      sizeSource: 'default',
      contentAspect: aspect,
    })
    onDimensionsChange?.(box)
  }, [metadataWidth, metadataHeight, onDimensionsChange, reportAspect, state.sizeSource])

  return (
    <NodeContainer
      nodeId={id}
      title={title || "Image"}
      icon="image"
      selected={selected}
      width={NODE_DIMENSIONS.ImageFile.width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
    >
      <CardContent
        className={`flex min-h-0 flex-1 flex-col items-stretch justify-center overflow-hidden p-1 transition-colors ${isDragOver ? 'bg-primary/10 border-2 border-primary border-dashed' : ''}`}
        style={{
          borderRadius: `${NODE_VISUAL_CONSTANTS.borderRadius.card}px`,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {(state?.generationStatus as string) === 'loading' ? (
          <div
            className="relative flex flex-col w-full aspect-square box-border items-center justify-center gap-3 rounded-[10px] bg-muted overflow-hidden"
            role="status"
            aria-label="Generating image content"
          >
            <div
              className="absolute inset-0 animate-[shimmer-sweep_2.5s_ease-in-out_infinite]"
              style={{
                background: 'linear-gradient(105deg, transparent 30%, rgba(139,92,246,0.08) 45%, rgba(139,92,246,0.15) 50%, rgba(139,92,246,0.08) 55%, transparent 70%)',
                backgroundSize: '200% 100%',
              }}
            />
            <span className="relative text-xs font-medium text-muted-foreground">
              {(state?.generationMessage as string) || 'Generating image...'}
            </span>
            <span className="relative text-[10px] tabular-nums text-muted-foreground" aria-live="polite" role="status">
              {Math.round((state?.generationProgress as number) ?? 0)}%
            </span>
          </div>
        ) : (state?.generationStatus as string) === 'error' ? (
          <div className="relative flex flex-col w-full aspect-square box-border items-center justify-center gap-2 rounded-[10px] bg-destructive/10 overflow-hidden">
            <Icon name="alert-circle" className="h-6 w-6 text-destructive" />
            <span className="text-xs text-destructive">Generation failed</span>
            <span className="text-[10px] text-red-400/60">Removing in a moment...</span>
          </div>
        ) : imageUrl ? (
          <div className="relative min-h-0 w-full flex-1">
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Uploaded"
              className="absolute inset-0 h-full w-full object-contain"
              style={{
                borderRadius: '10px',
              }}
              onLoad={handleImageLoad}
            />

            {/* Loading overlay */}
            {(isLoadingImage || isUploading) && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Icon name="loader-circle" className="h-8 w-8 text-white animate-spin" />
              </div>
            )}

            {/* Hover buttons */}
            {isHovered && !isLoadingImage && !isUploading && (
              <div className="absolute top-2 right-2 flex gap-1">
                {/* Download button - Always visible */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 rounded-[6px] p-0 bg-card hover:bg-accent"
                  onClick={async (e) => {
                    e.stopPropagation()
                    try {
                      const dlMetadata = state?.fileMetadata as FileMetadata | undefined
                      // Download original version (high quality), not low-res
                      const originalUrl = dlMetadata?.versions?.original?.url
                      const downloadUrl = originalUrl || imageUrl
                      if (!downloadUrl) return

                      // Fetch and download
                      const response = await fetch(downloadUrl)
                      const blob = await response.blob()
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = dlMetadata?.filename || `image-${id}.png`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      window.URL.revokeObjectURL(url)
                    } catch (err) {
                      console.error('Failed to download image:', err)
                    }
                  }}
                  title="Download original"
                  aria-label="Download original image"
                >
                  <Icon name="download" className="h-4 w-4" />
                </Button>

                {/* Replace/Remove buttons - Hidden if node has parent */}
                {!hasParent && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 rounded-[6px] p-0 bg-card hover:bg-accent"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleImageUpload()
                      }}
                      title="Replace image"
                      aria-label="Replace image"
                    >
                      <Icon name="upload" className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 rounded-[6px] p-0 bg-card hover:bg-accent"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleImageRemove()
                      }}
                      title="Remove image"
                      aria-label="Remove image"
                    >
                      <Icon name="x" className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleImageUpload()
            }}
            disabled={isUploading || isLoadingImage}
            className="flex flex-col w-full aspect-square box-border items-center justify-center gap-3 border-2 border-dashed border-border transition-colors hover:border-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              borderRadius: `${NODE_VISUAL_CONSTANTS.borderRadius.button.large}px`,
            }}
          >
            {isUploading || isLoadingImage ? (
              <>
                <Icon name="loader-circle" className="h-6 w-6 text-muted-foreground animate-spin" />
                <div className="flex flex-col items-center gap-1">
                  <span className="text-sm text-muted-foreground">
                    Uploading...
                  </span>
                  {uploadProgress !== null && (
                    <span className="text-xs text-muted-foreground">
                      {uploadProgress}%
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <Icon name="upload" className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center gap-1">
                  <span className="text-sm text-muted-foreground">
                    {error ? 'Upload failed. Click to retry' : 'Drag and drop your file here'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    PNG, JPEG. Up to 20mb.
                  </span>
                </div>
              </>
            )}
          </button>
        )}
      </CardContent>
    </NodeContainer>
  )
}

// Export optimized version
export const ImageFile = withNodeOptimization(ImageFileComponent, {
  compareProps: ['id', 'title', 'selected', 'fileUuid', 'canvasId', 'state', 'zoom']
})
